<template>
    <div id="activeCall" class="relative h-full w-full flex flex-col items-center justify-center">
        <div class="window-drag-zone fixed top-0 inset-x-0 h-7"></div>

        <div v-if="flags.loading" class="flex flex-col items-center justify-center min-h-full space-y-4">
            <UIcon name="i-ph-circle-dashed" class="size-6 text-white animate-spin-slow" />
            <span class="font-mono text-sm text-white">
                Connecting Call...
            </span>
            <UButton @click="dismiss()" size="sm" color="white">
                Cancel
            </UButton>
        </div>

        <div v-else-if="errorCode" class="flex flex-col items-center justify-center min-h-full rounded-2xl">
            <div class="flex items-center justify-center h-16 w-16 rounded-full bg-gray-900 bg-opacity-70 text-white">
                <UIcon name="i-ph-warning-duotone" class="size-8" />
            </div>
            <p class="mt-6 text-center text-sm text-white">
                {{ callError }}
            </p>
            <p class="mt-2 font-mono text-xs text-gray-400">
                [ERR-{{ errorCode }}]
            </p>
            <button @click="dismiss()" class="mt-6 rounded-lg px-3.5 py-1.5 text-sm font-medium text-white border border-white hover:text-gray-800 hover:bg-white transition-colors">
                Go Back
            </button>
        </div>

        <div v-else-if="call" class="min-h-full w-full">
            <div id="activeCallContents" class="h-[calc(100vh-96px)] flex gap-6 p-6 pb-0">
                <aside class="w-1/4 h-full relative flex flex-col items-end justify-between gap-6">
                    <CallCustomer :customer="call.customer" :exclusion="customerExclusion" :switched-call-count="callerSwitchedCallCount" @switch="switchingCustomer = true" @subscribe="subscribingCustomer = true" class="flex-grow" />
                    <CallAddress :address="call.address" class="shrink-0" />
                </aside>
    
                <main class="relative flex-1 h-full rounded-lg bg-gray-950/75 overflow-hidden">
                    <div v-if="callIsExternal || callDuration" class="absolute flex items-center justify-center px-3 py-1 top-5 left-1/2 transform -translate-x-1/2 rounded-full bg-gray-600 bg-opacity-30">
                        <span class="text-xs text-white font-mono font-medium">
                            {{ callIsExternal ? 'External Call' : callDuration }}
                        </span>
                    </div>

                    <div v-if="flags.hasParticipant" class="relative h-full w-full">
                        <div 
                            v-show="flags.participantCameraActive" 
                            id="callerVideo" 
                            ref="callerVideo" 
                            class="h-full flex items-center justify-center"
                        />

                        <div v-if="!flags.participantCameraActive" class="flex items-center justify-center h-full w-full">
                            <Avatar
                                size="3xl"
                                class="rounded-full flex-shrink-0 ring-1 ring-gray-400 bg-gray-200 overflow-hidden"
                                :path="call.customer.avatar"
                                :placeholder="[call.customer.first_name, call.customer.last_name].filter(Boolean).join(' ')"
                            />
                        </div>

                        <div v-if="callHasVideo && !flags.participantMicrophoneActive" class="absolute flex items-center justify-center bottom-5 right-5 rounded-full bg-red-500 w-8 h-8">
                            <UIcon name="i-mdi-microphone-off" class="size-4 text-white" />
                        </div>
                    </div>
                    <div v-else class="flex items-center justify-center h-full w-full">
                        <span class="text-gray-100 text-sm font-mono">Waiting to connect...</span>
                    </div>

                    <div v-if="flags.localCameraActive" class="absolute bottom-5 left-5 z-20">
                        <p class="text-white text-xs mb-1.5 font-medium font-mono">You</p>
                        <div class="h-24 w-40 rounded-lg bg-gray-950 overflow-hidden">
                            <div id="myVideo" ref="myVideo" class="relative h-full -scale-x-100"></div>
                        </div>
                    </div>
                </main>
    
                <aside class="w-1/4 h-full rounded-lg text-white bg-gray-950/50 overflow-hidden">
                    <CallReports ref="callReportsRef" :call="call" />
                </aside>
            </div>
    
            <div class="h-24 w-full px-6 grid grid-cols-3">
                <div class="flex shrink-0 items-center space-x-3">
                    <img class="size-12 opacity-90" src="@/assets/img/logo.png" />
                    <span class="text-gray-200 font-mono">{{ currentTime }}</span>
                </div>
                <div class="flex items-center justify-center space-x-3 text-white">
                    <UTooltip v-if="callHasVideo" :text="flags.localMicrophoneActive ? 'Mute Microphone' : 'Unmute Microphone'">
                        <UButton
                            @click="toggleMicrophone(!flags.localMicrophoneActive)"
                            :ui="{ rounded: 'rounded-full' }"
                            :icon="flags.localMicrophoneActive ? 'i-mdi-microphone' : 'i-mdi-microphone-off'"
                            :class="flags.localMicrophoneActive ? 'dark:text-white dark:bg-gray-700 dark:hover:bg-gray-600' : 'dark:text-white dark:bg-red-500 dark:hover:bg-red-600'"
                            size="xl"
                        />
                    </UTooltip>

                    <CallEndButton
                        @confirm="dismiss()"
                        @report-incomplete="onReportIncomplete"
                        :call-id="call.id"
                    />

                    <UTooltip v-if="callHasVideo" :text="flags.localCameraActive ? 'Hide Camera' : 'Show Camera'">
                        <UButton
                            @click="toggleCamera(!flags.localCameraActive)"
                            :ui="{ rounded: 'rounded-full' }"
                            :icon="flags.localCameraActive ? 'i-mdi-video' : 'i-mdi-video-off'"
                            :class="flags.localCameraActive ? 'dark:text-white dark:bg-gray-700 dark:hover:bg-gray-600' : 'dark:text-white dark:bg-red-500 dark:hover:bg-red-600'"
                            size="xl"
                        />
                    </UTooltip>
                </div>
                <div class="flex items-center justify-end space-x-2 text-white">
                    <CallHoldButton
                        @confirm="dismiss()"
                        :call="call"
                    />
                    <UButton
                        v-if="callIsExternal"
                        @click="openShareDialog()"
                        :ui="{ rounded: 'rounded-full' }"
                        label="Share Call"
                        icon="i-heroicons-share"
                        size="lg"
                        class="dark:text-white dark:bg-green-500 dark:hover:bg-green-600"
                    />
                    <UButton
                        @click="openTransferDialog()"
                        :ui="{ rounded: 'rounded-full' }"
                        label="Transfer"
                        icon="i-ph-phone-outgoing-duotone"
                        size="lg"
                        class="dark:text-white dark:bg-blue-500 dark:hover:bg-blue-600"
                    />
                </div>
            </div>
        </div>
    </div>

    <CallSubscribeModal v-model:open="subscribingCustomer" @subscribed="onCustomerSubscribed()" :call-id="call?.id" :customer-id="call?.customer?.id" />
    <CallTransferModal v-model:open="transferring" @transferred="dismiss()" :call-id="call?.id" />
    <CallSwitchCustomerModal v-model:open="switchingCustomer" @switched="onCustomerSwitched()" :call-id="call?.id" :current-customer-id="call?.customer?.id" />
    <CallShareCallModal v-model:open="sharingCall" @shared="onCallShared" :call="call" />
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import TwilioVideo from 'twilio-video'
import { UserRole } from '~/types/user'
import { nativeWindow, platform } from '@todesktop/client-core'
import { CallSource, CallStatus, type Call } from '~/types/call'
import { SubscriptionSourceType } from '~/types/subscription'

definePageMeta({ layout: 'call', middleware: 'auth', roles: [UserRole.admin, UserRole.medicalDirector, UserRole.doctor, UserRole.operator] })
useHead({ title: 'Active Call' })

const auth = useAuthStore()
const route = useRoute()
const client = useSupabaseClient()

const { state: callCenterState } = useCallCenter()

var room: TwilioVideo.Room|null = null

const call = ref<Call>()
const customerExclusion = ref<string>('')
const callerSwitchedCallCount = ref(0)
const callDuration = ref<string>()
const currentTime = ref<string>(dayjs().format('hh:mm A'))
const errorCode = ref<number>()
const transferring = ref<boolean>(false)
const subscribingCustomer = ref<boolean>(false)
const switchingCustomer = ref<boolean>(false)
const sharingCall = ref<boolean>(false)
const myVideo = ref<HTMLElement>()
const callerVideo = ref<HTMLElement>()
const callReportsRef = ref<any>()

function onReportIncomplete() {
    callReportsRef.value?.openEditor?.()
}

async function logCallEvent(type: string, opts: { participant?: string; metadata?: Record<string, any> } = {}) {
    try {
        await client.from('call_events').insert({
            call_id: route.params.id as string,
            type,
            actor_user_id: auth.user?.id ?? null,
            participant: opts.participant ?? null,
            metadata: opts.metadata ?? {},
        } as never)
    } catch (e) {
        console.error('[call] logCallEvent failed', e)
    }
}

const timers = reactive({
    callDuration: undefined,
    currentTime: undefined
} as {
    callDuration?: NodeJS.Timeout
    currentTime?: NodeJS.Timeout
})

const flags = reactive({
    loading: false,
    hasParticipant: false,
    localMicrophoneActive: false,
    localCameraActive: false,
    participantMicrophoneActive: false,
    participantCameraActive: false,
})

const callIsExternal = computed(() => {
    return call.value?.source === CallSource.external
})

const callHasVideo = computed(() => {
    return call.value?.source === CallSource.app || (call.value?.source === CallSource.external && call.value?.room_sid)
})

const callError = computed(() => {
    if (errorCode.value) {
        switch (errorCode.value) {
            case 400: return "You're already on another call."
            case 402: return "An active call is already open on another tab."
            case 403: return "The call you're trying to join has ended."
            case 406:
            case 404: return "The call you're trying to join is invalid."
            case 405: return "The call you're trying to join is occupied by another user."
            default: return "An unknown error has occurred."
        }
    }
})

onMounted(async () => {
    console.debug('[DEBUG] call/[id].vue onMounted() - Component mounted, starting call process', { 
        routeId: route.params.id,
        userId: auth.user?.id 
    })
    await fetchCall()
    await connectCall()
})

onUnmounted(() => {
    callCenterState.value.activeCallId = undefined
    callCenterState.value.activeWindowId = undefined
    clearInterval(timers.callDuration)
    clearInterval(timers.currentTime)
})

async function fetchCall() {
    const id = route.params.id as string
    console.debug('[DEBUG] call/[id].vue fetchCall() - Fetching call data', { callId: id })

    const { data, error } = await client
        .from('calls')
        .select(`
            *, 
            customer:customers (
                *,
                reports:call_reports (
                    *,
                    user:users (id, name, role),
                    call:calls (id, number),
                    call_reason:options!call_reports_call_reason_id_fkey (id, label, description),
                    call_decision:options!call_reports_call_decision_id_fkey (id, label, description),
                    diagnosis:options!call_reports_diagnosis_id_fkey (id, label, description, metadata)
                ),
                subscriptions (
                    id,
                    partner_id,
                    source_id,
                    source_type,
                    is_active,
                    expires_at,
                    partner:partners (id, name, avatar)
                )
            ), 
            address:addresses (*), 
            user:users!calls_user_id_fkey (id, name, role),
            reports:call_reports (
                *,
                user:users (id, name, role),
                call:calls (id, number),
                call_reason:options!call_reports_call_reason_id_fkey (id, label, description),
                call_decision:options!call_reports_call_decision_id_fkey (id, label, description),
                diagnosis:options!call_reports_diagnosis_id_fkey (id, label, description, metadata)
            )
        `)
        .is('customers.subscriptions.is_active', true)
        .limit(1, { referencedTable: 'customers.subscriptions' })
        .eq('id', id)
        .maybeSingle<Call>()

    if (data) {
        console.debug('[DEBUG] call/[id].vue fetchCall() - Call data retrieved successfully', { 
            callId: data.id,
            callNumber: data.number,
            callStatus: data.status,
            callSource: data.source,
            customerId: data.customer?.id,
            customerName: data.customer ? `${data.customer.first_name} ${data.customer.last_name}` : 'Unknown',
            hasUser: !!data.user,
            userId: data.user?.id,
            userName: data.user?.name
        })
        call.value = data as Call

        if (call.value.reports && call.value.customer.reports) {
            call.value.reports.push(...call.value.customer.reports)
        }

        call.value.reports?.forEach((report) => {
            if (report.call_reason) report.call_reason.label = [report.call_reason.label, report.call_reason.description].filter(Boolean).join(' / ')
            if (report.diagnosis) report.diagnosis.label = [report.diagnosis.label, report.diagnosis.description].filter(Boolean).join(' / ')
            if (report.call_decision) report.call_decision.label = [report.call_decision.label, report.call_decision.description].filter(Boolean).join(' / ')
        })

        // exclusion is a credential field (not a customer column); resolve it from the
        // active subscription's source credential so the call panel can display it.
        customerExclusion.value = ''
        const activeSubscription = call.value.customer.subscriptions?.[0]
        if (activeSubscription?.source_type === SubscriptionSourceType.credential && activeSubscription.source_id) {
            const { data: credential } = await client.rpc('get_subscription_source_credentials', { p_subscription_ids: [activeSubscription.id] } as never)
                .single<{ fields: Record<string, string> }>()

            if (credential?.fields?.exclusion) {
                customerExclusion.value = credential.fields.exclusion
            }
        }

        // the badge describes the caller, not call.customer -- the latter becomes the
        // beneficiary the moment an operator switches, hiding the warning when it matters.
        callerSwitchedCallCount.value = 0
        if (call.value.caller_user_id) {
            const { data: caller } = await client
                .from('customers')
                .select('id')
                .eq('user_id', call.value.caller_user_id)
                .maybeSingle()

            if (caller) {
                const { data: counts } = await client
                    .from('membership_switch_counts')
                    .select('switched_call_count')
                    .eq('customer_id', (caller as unknown as { id: string }).id)
                    .maybeSingle()

                callerSwitchedCallCount.value = (counts as unknown as { switched_call_count: number } | null)?.switched_call_count ?? 0
            }
        }

        console.debug('[DEBUG] call/[id].vue fetchCall() - Call data processing complete')
    } 
    else if (error) {
        console.error('[DEBUG] call/[id].vue fetchCall() - Failed to fetch call data', error)
    } else {
        console.debug('[DEBUG] call/[id].vue fetchCall() - No call data returned, no error')
    }
}

async function connectCall() {
    console.debug('[DEBUG] call/[id].vue connectCall() - Starting call connection process', { 
        callId: call.value?.id,
        hasCall: !!call.value,
        callStatus: call.value?.status,
        callSource: call.value?.source,
        callEndedAt: call.value?.ended_at,
        currentUserId: auth.user?.id,
        callUserId: call.value?.user?.id
    })
    
    if (!call.value) {
        console.debug('[DEBUG] call/[id].vue connectCall() - No call found, setting error 404')
        errorCode.value = 404
        return
    }
    else if (call.value.ended_at) {
        console.debug('[DEBUG] call/[id].vue connectCall() - Call already ended, setting error 403', { endedAt: call.value.ended_at })
        errorCode.value = 403
        return
    }
    else if (call.value.status === CallStatus.active && call.value.user?.id !== auth.user?.id) {
        console.debug('[DEBUG] call/[id].vue connectCall() - Call active by different user, setting error 401', { 
            callUserId: call.value.user?.id, 
            currentUserId: auth.user?.id 
        })
        errorCode.value = 401
        return
    }

    flags.loading = true
    console.debug('[DEBUG] call/[id].vue connectCall() - Call connection started', { callId: call.value.id })

    try {
        console.debug('[DEBUG] call/[id].vue connectCall() - Requesting connection token from API')
        const { token } = await $fetch<{ token?: string }>('/api/calls/connect', {
            method: 'POST',
            body: { id: call.value.id }
        })
        console.debug('[DEBUG] call/[id].vue connectCall() - Connection token received', { hasToken: !!token, tokenLength: token?.length })

        if (token) {
            console.debug('[DEBUG] call/[id].vue connectCall() - Token received, connecting to Twilio Video')
            room = await TwilioVideo.connect(token)
            console.debug('[DEBUG] call/[id].vue connectCall() - Twilio Video connection established', {
                roomSid: room?.sid,
                roomName: room?.name,
                participantCount: room?.participants?.size
            })

            if (room) {
                console.debug('[DEBUG] call/[id].vue connectCall() - Setting up participant event handlers')
                logCallEvent('participant_joined', { participant: 'operator' })
                room.participants?.forEach((participant) => {
                    console.debug('[DEBUG] call/[id].vue connectCall() - Processing existing participant', {
                        participantSid: participant.sid,
                        participantIdentity: participant.identity
                    })
                    onParticipantConnected(participant)
                })
                room.on('participantConnected', (participant) => {
                    console.debug('[DEBUG] call/[id].vue connectCall() - New participant connected', {
                        participantSid: participant.sid,
                        participantIdentity: participant.identity
                    })
                    logCallEvent('participant_joined', { participant: 'customer', metadata: { identity: participant.identity } })
                    onParticipantConnected(participant)
                })
                room.on('participantDisconnected', (participant) => {
                    console.debug('[DEBUG] call/[id].vue connectCall() - Participant disconnected', {
                        participantSid: participant.sid,
                        participantIdentity: participant.identity
                    })
                    logCallEvent('participant_left', { participant: 'customer', metadata: { identity: participant.identity } })
                    flags.hasParticipant = false
                })
            }

            console.debug('[DEBUG] call/[id].vue connectCall() - Enabling microphone and camera')
            toggleMicrophone(true)
            toggleCamera(true)

            console.debug('[DEBUG] call/[id].vue connectCall() - Video call connection complete', {
                callId: call.value.id,
                roomSid: room?.sid
            })
        } else {
            console.debug('[DEBUG] call/[id].vue connectCall() - No token received, treating as external call without video')
            flags.hasParticipant = true
        }
    } catch (error: any) {
        console.error('[DEBUG] call/[id].vue connectCall() - Call connection failed', { 
            error: error.message,
            statusCode: error.statusCode,
            code: error.code,
            callId: call.value?.id
        })
        errorCode.value = error.statusCode || error.code
    }

    console.debug('[DEBUG] call/[id].vue connectCall() - Initializing timers and finishing connection')
    initTimers()
    flags.loading = false
}

function initTimers() {
    if (call.value) {
        timers.callDuration = setInterval(() => {
            callDuration.value = dayjs().startOf('day').add(dayjs().diff(call.value?.connected_at || call.value?.created_at, 'seconds'), 'seconds').format('mm:ss')
        }, 1000)

        timers.currentTime = setInterval(() => {
            currentTime.value = dayjs().format("hh:mm A")
        }, 5000)
    }
}

function toggleMicrophone(active: boolean) {
    if (flags.localMicrophoneActive !== active) {
        flags.localMicrophoneActive = active

        if (room) {
            if (flags.localMicrophoneActive) {
                room.localParticipant?.audioTracks.forEach((publication) => publication.track.enable())
            }
            else {
                room.localParticipant?.audioTracks.forEach((publication) => publication.track.disable())
            }
        }
    }
}

function toggleCamera(active: boolean) {
    if (flags.localCameraActive !== active) {
        flags.localCameraActive = active

        if (room) {
            if (flags.localCameraActive) {
                if (room.localParticipant.videoTracks && room.localParticipant.videoTracks.size) {
                    const [publication] = room.localParticipant.videoTracks.values()
                    publication.track.enable()
                    setTimeout(() => {
                        if (myVideo.value) {
                            myVideo.value.appendChild(publication.track.attach())
                        }
                    }, 200)
                }
                else {
                    TwilioVideo.createLocalVideoTrack().then((track) => {
                        return room?.localParticipant.publishTrack(track)
                    }).then((publication) => {
                        if (publication && myVideo.value) {
                            myVideo.value.appendChild((publication.track as any).attach())
                        }
                    })
                }
            }
            else {
                room.localParticipant.videoTracks.forEach((publication) => {
                    publication.track.stop()
                    publication.unpublish()
                })
            }
        }
    }
}

function openTransferDialog() {
    transferring.value = true
    toggleMicrophone(false)
    toggleCamera(false)
}

function openShareDialog() {
    sharingCall.value = true
}

async function onCallShared(room_sid: string) {
    if (call.value) {
        call.value.room_sid = room_sid

        if (!room) {
            const { token } = await $fetch<{ token?: string }>('/api/calls/connect', {
                method: 'POST',
                body: { id: call.value.id }
            })

            if (token) {
                console.debug('[DEBUG] call/[id].vue onCallShared() - Connecting to Twilio Video with token')

                flags.hasParticipant = false

                room = await TwilioVideo.connect(token)

                if (room) {
                    console.debug('[DEBUG] call/[id].vue onCallShared() - Setting up participant event handlers')

                    room.participants?.forEach((participant) => {
                        console.debug('[DEBUG] call/[id].vue onCallShared() - Processing existing participant', {
                            participantSid: participant.sid,
                            participantIdentity: participant.identity
                        })
                        onParticipantConnected(participant)
                    })
                    room.on('participantConnected', (participant) => {
                        console.debug('[DEBUG] call/[id].vue onCallShared() - New participant connected', {
                            participantSid: participant.sid,
                            participantIdentity: participant.identity
                        })
                        logCallEvent('participant_joined', { participant: 'guest', metadata: { identity: participant.identity } })
                        onParticipantConnected(participant)
                    })
                    room.on('participantDisconnected', (participant) => {
                        console.debug('[DEBUG] call/[id].vue onCallShared() - Participant disconnected', {
                            participantSid: participant.sid,
                            participantIdentity: participant.identity
                        })
                        logCallEvent('participant_left', { participant: 'guest', metadata: { identity: participant.identity } })
                        flags.hasParticipant = false
                    })
                }

                console.debug('[DEBUG] call/[id].vue onCallShared() - Enabling microphone and camera')
                toggleMicrophone(true)
                toggleCamera(true)
            }
        }
    }

    sharingCall.value = false
}

async function onCustomerSubscribed() {
    await fetchCall()
    subscribingCustomer.value = false
}

async function onCustomerSwitched() {
    await fetchCall()
    switchingCustomer.value = false
}

function onParticipantConnected(participant: TwilioVideo.Participant) {
    flags.hasParticipant = true

    participant.tracks.forEach((publication: any) => {
        if (publication.isSubscribed) {
            handleTrackSubscribed(publication.track)
        }

        publication.on('subscribed', (track: any) => handleTrackSubscribed(track))
        publication.on('unsubscribed', (track: any) => handleTrackUnsubscribed(track))
    })
    
    console.log(`A remote Participant connected: ${participant}`)
}

function handleTrackSubscribed(track: TwilioVideo.VideoTrack | TwilioVideo.AudioTrack) {
    if ((track.kind == "audio" || track.kind == "video") && callerVideo.value) {
        callerVideo.value.appendChild(track.attach())
    }

    if (track.isEnabled) {
        if (track.kind == "audio")
            flags.participantMicrophoneActive = true
        else if (track.kind == "video")
            flags.participantCameraActive = true
    }

    track.on('enabled', () => {
        if (track.kind == "audio")
            flags.participantMicrophoneActive = true
        else if (track.kind == "video")
            flags.participantCameraActive = true
    })

    track.on('disabled', () => {
        if (track.kind == "audio")
            flags.participantMicrophoneActive = false
        else if (track.kind == "video")
            flags.participantCameraActive = false
    })
}

function handleTrackUnsubscribed(track: TwilioVideo.VideoTrack | TwilioVideo.AudioTrack) {
    if (track.kind == "audio")
        flags.participantMicrophoneActive = false
    if (track.kind == "video")
        flags.participantCameraActive = false
}

function disconnectRoom() {
    if (room) {
        room.localParticipant.tracks.forEach((publication: any) => {
            publication.track.disable()
            publication.track.stop()
            publication.unpublish()
        })

        logCallEvent('participant_left', { participant: 'operator' })
        room.disconnect()
    }
}

function dismiss() {
    callCenterState.value.activeCallId = undefined
    callCenterState.value.activeWindowId = undefined
    disconnectRoom()

    if (timers.callDuration) {
        clearInterval(timers.callDuration)
    }
    if (timers.currentTime) {
        clearInterval(timers.currentTime)
    }

    if (platform.todesktop.isDesktopApp()) {
        nativeWindow.close()
    } else {
        window.close()
    }
}
</script>
